"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";

// Client-cache prefetch entries expire (Next staleTimes), so a warm-once pass
// goes cold during a normal dwell — exactly the "first click after idling is
// slow" disease. Re-warm on mount, whenever the tab becomes visible again, and
// on a gentle interval while visible, so the next click commits its URL from
// cache instead of waiting on the network. A prefetch of a fresh entry is one
// CDN hit for a static shell; Next dedupes anything still warm.
const REWARM_INTERVAL_MS = 4 * 60 * 1000;

export function useRouteWarmer(routes: readonly string[]) {
  const router = useRouter();

  useEffect(() => {
    const warm = () => {
      if (document.visibilityState === "hidden") return;
      for (const route of routes) router.prefetch(route);
    };

    warm();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") warm();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = setInterval(warm, REWARM_INTERVAL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(interval);
    };
    // Spread the routes so a caller passing a fresh array literal each render
    // doesn't tear down and recreate the interval every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, ...routes]);
}
